import { CREW_MEMBER_STATUSES, NA_LABEL } from 'pages/RideListPage/const';
import { formatDateDisplay, formatTime } from 'utils/datetime';

import { DEADHEAD_ITEM_TEXT } from './texts';

/**
 * @typedef {object} StationDetails
 * @property {string} [code]
 * @property {string} [name]
 */

/**
 * @typedef {object} DeadheadLocation
 * @property {StationDetails} [stationDetails]
 * @property {string} [address]
 * @property {string} [plannedTimestamp] ISO-8601 UTC.
 * @property {string} [timezone] IANA tz.
 */

/**
 * @typedef {object} DeadheadCrewMember
 * @property {string} [id]
 * @property {string} [fullName]
 */

/**
 * @typedef {'COMPLETED' | 'IN_PROGRESS' | string} DeadheadStatus
 */

/**
 * @typedef {object} DeadheadInput
 * @property {string} [id]
 * @property {string} [rideNumber]
 * @property {string} [type]
 * @property {DeadheadStatus} [status]
 * @property {string} [busNumber]
 * @property {DeadheadCrewMember} [crewMember]
 * @property {DeadheadLocation} [departure]
 * @property {DeadheadLocation} [arrival]
 */

/**
 * @typedef {object} TimezoneSelection
 * @property {string | null} value Null = use station timezone.
 */

/**
 * @typedef {'completed' | 'inProgress' | undefined} StatusVisualKey
 */

/**
 * @typedef {object} DeadheadRowData
 * @property {string} rideNumber
 * @property {string} type
 * @property {string} busLabel Bus number or fallback.
 * @property {string} crewMemberName Full name or empty string.
 * @property {string} routeLabel `"from → to"`.
 * @property {string} departureLabel `"Departure: <time> (<tz>)"`.
 * @property {string} timezoneSelected Resolved IANA tz.
 * @property {StatusVisualKey} statusVisualKey Key for tag appearance + StatusIcon.
 * @property {string | undefined} statusLabel Human label from CREW_MEMBER_STATUSES.
 */

/**
 * @param {DeadheadLocation | null | undefined} location
 * @returns {string}
 */
const getLocation = (location) => {
	if (location?.stationDetails?.code && location?.stationDetails?.name) {
		return `${location.stationDetails.code} (${location.stationDetails.name})`;
	}
	return location?.address ?? '';
};

/**
 * @param {DeadheadStatus | undefined} status
 * @returns {StatusVisualKey}
 */
const toStatusVisualKey = (status) => {
	if (status === 'COMPLETED') return 'completed';
	if (status === 'IN_PROGRESS') return 'inProgress';
	return undefined;
};

/**
 * Build the display data for a single deadhead row.
 *
 * @param {object} args
 * @param {DeadheadInput} args.deadhead
 * @param {TimezoneSelection} args.timezone
 * @returns {DeadheadRowData}
 */
export const buildDeadheadRowData = ({ deadhead, timezone }) => {
	const { departure, arrival, type, crewMember, status, rideNumber, busNumber } = deadhead ?? {};

	const timezoneSelected = timezone?.value === null ? departure?.timezone : timezone?.value;

	const from = getLocation(departure);
	const to = getLocation(arrival);
	const formattedTime = `${formatDateDisplay(departure?.plannedTimestamp, { timezone: timezoneSelected })} - ${formatTime(departure?.plannedTimestamp, { timezone: timezoneSelected })}`;

	return {
		rideNumber: rideNumber ?? '',
		type: type ?? '',
		busLabel: busNumber ?? NA_LABEL,
		crewMemberName: crewMember?.fullName ?? '',
		routeLabel: DEADHEAD_ITEM_TEXT.route(from, to),
		departureLabel: DEADHEAD_ITEM_TEXT.departure(formattedTime, timezoneSelected ?? ''),
		timezoneSelected: timezoneSelected ?? '',
		statusVisualKey: toStatusVisualKey(status),
		statusLabel: CREW_MEMBER_STATUSES[status],
	};
};
